
























import { Vue, Component, Prop, Model, Watch } from 'vue-property-decorator';
import { getArchiveTreeByParent, getRgFileSetting, searchNodesByKw as searchArchiveNodesByKw } from '@/api/cti';

interface CascaderOption {
    value: number;
    label: string;
    isLeaf: boolean;
    children?: CascaderOption[];
}

interface ArchiveNode {
    id: number;
    name: string;
    inUse: boolean;
    leaf: boolean;
    children?: ArchiveNode[];
}

const mapNodeToCascaderOpt = (node: ArchiveNode): CascaderOption => {
    return {
        value: node.id,
        label: node.name,
        isLeaf: node.leaf
    };
};

const normalizeArchiveTree = (rawNode: ArchiveNode): CascaderOption => {
    const { id, name, leaf, children } = rawNode;
    if (!leaf && children && children.length > 0) {
        return {
            value: id,
            label: name,
            children: children.map(normalizeArchiveTree),
            isLeaf: false
        };
    } else {
        return { value: id, label: name, isLeaf: true };
    }
};

@Component
export default class ArchiveFilter extends Vue {
    @Model('change', { type: Array, default: () => [] })
    readonly cascaderVal!: any[];

    @Prop({ default: 0 })
    readonly rgId!: number;

    cascaderData: CascaderOption[] = [];
    treeData: CascaderOption | null = null;
    searching: boolean = false;
    rootNodeOption: CascaderOption | null = null;

    // 内部值
    internalValue: number[] = [];

    async created() {
        this.internalValue = this.getSelectedNodeIds();
        if (this.rgId) {
            await this._initArchiveTree();
        }
    }

    @Watch('rgId', { immediate: true })
    async watchRgId(rgId: number) {
        if (rgId) {
            await this._initArchiveTree();
        }
    }

    @Watch('cascaderVal', { immediate: true })
    watchCascaderVal(val: any[]) {
        this.internalValue = this.getSelectedNodeIds();
    }

    async _initArchiveTree() {
        try {
            const { data, code } = await getRgFileSetting(this.rgId);
            if (code === 200 && data.rootNode) {
                const rootNode = data.rootNode;
                const rootNodeOption = mapNodeToCascaderOpt(rootNode);
                this.rootNodeOption = rootNodeOption;

                const _treeData = await this.loadTree(rootNode.id);
                this.treeData = _treeData;
                this.cascaderData = [_treeData];
            }
        } catch (error) {
            console.error('Failed to load archive tree:', error);
        }
    }

    async loadTree(parentId: number) {
        const res = await getArchiveTreeByParent(parentId);
        const rawNode = res.data;
        const _treeData = normalizeArchiveTree(rawNode);
        return _treeData;
    }

    handleCascaderValChange(val: number[], selectedOptions: any[]) {
        if (val.length === 0) {
            this.$emit('change', []);
            this.cascaderData = this.treeData ? [this.treeData] : [];
            return;
        }

        const selectedItems = this.convertToObjectArray(val, selectedOptions);
        this.$emit('change', selectedItems);
    }

    /**
     * 从 cascaderVal 中提取节点ID数组
     */
    getSelectedNodeIds(): number[] {
        if (!this.cascaderVal || this.cascaderVal.length === 0) {
            return [];
        }
        return this.cascaderVal.map(item => {
            if (typeof item === 'object' && item.id) {
                return item.id;
            }
            return item;
        });
    }

    /**
     * 将节点ID数组转换为对象数组
     */
    convertToObjectArray(nodeIds: number[], selectedOptions: any[]): any[] {
        return nodeIds.map((nodeId, index) => {
            const pathOptions = selectedOptions && selectedOptions[index];
            if (pathOptions && Array.isArray(pathOptions)) {
                const namePath = pathOptions.map(option => option.label).join(' / ');
                return {
                    id: nodeId,
                    namePath: namePath
                };
            }
            return {
                id: nodeId,
                namePath: ''
            };
        });
    }

    searchNodesByKw(query: string) {
        if (query === '') {
            this.cascaderData = this.treeData ? [this.treeData] : [];
            return;
        }

        this.searching = true;
        const rootNodeId = this.rootNodeOption?.value || 0;

        searchArchiveNodesByKw(rootNodeId, query, false)
            .then((response: any) => {
                console.log('Search response:', response);
                const { data, code } = response;
                if (code === 200) {
                    interface ResponseItem {
                        fullName: string;
                        id: number;
                        leaf: boolean;
                        name: string;
                        pathList: number[];
                    }

                    const items: ResponseItem[] = data.items || [];
                    console.log('Search items:', items);

                    const result: CascaderOption[] = items.map(item => {
                        return {
                            value: item.id,
                            label: item.fullName,
                            isLeaf: true
                        };
                    });

                    this.cascaderData = result;
                    console.log('Updated cascaderData:', this.cascaderData);
                } else {
                    console.warn('Search returned non-200 code:', code);
                }
            })
            .catch((error: any) => {
                console.error('Failed to search nodes:', error);
            })
            .finally(() => {
                this.searching = false;
            });
    }
}
